import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
  snapshotAccounts,
  venueAccountKey,
  type OpenerDescalceAccountMeta,
  type OpenerDescalceSnapshot
} from '../../api/useOpenerDescalce';
import type { DescalceTotals } from './descalceFilters';
import { TriageIndexNotice } from '../triage/TriageIndexNotice';
import type { UseTriageIndexResult } from '../../api/useTriageIndex';

const PLATFORM_LABEL: Record<OpenerDescalceAccountMeta['platform'], string> = {
  kalshi: 'Kalshi',
  polymarket: 'Poly'
};

const SOURCE_COLOR: Record<
  OpenerDescalceAccountMeta['source'],
  'success' | 'warning' | 'error' | 'default'
> = {
  venue: 'success',
  starting: 'warning',
  unconfigured: 'default',
  error: 'error'
};

/**
 * One chip per venue-account the snapshot carries — never a fixed pair. The
 * fleet is data: a build that renders a hardcoded number of chips silently
 * drops every account past it, and an account whose read is failing would then
 * be invisible on the one screen meant to catch it.
 */
const AccountChip = ({ account }: { account: OpenerDescalceAccountMeta }) => {
  const lastSuccess =
    account.lastSuccessAt != null
      ? new Date(account.lastSuccessAt).toLocaleTimeString()
      : 'never';
  const title =
    `${PLATFORM_LABEL[account.platform]} account "${account.accountId}" · source: ${account.source} · last success: ${lastSuccess}` +
    (account.redeemableExcluded
      ? ` · ${account.redeemableExcluded} redeemable row(s) excluded from counts`
      : '') +
    (account.lastError ? ` · ${account.lastError}` : '');
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        label={`${PLATFORM_LABEL[account.platform]} ${account.accountId}: ${account.source}`}
        color={SOURCE_COLOR[account.source] ?? 'default'}
        variant="outlined"
        data-account={venueAccountKey(account.platform, account.accountId)}
        data-account-source={account.source}
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    </Tooltip>
  );
};

/**
 * The refused rules' group list, or `''`.
 *
 * 🚨 `Array.isArray` before `.join`, the same idiom `snapshotAccounts` uses:
 * the payload is an unvalidated cast, a `.length` truthy check passes for a
 * STRING or for `{length: 2}`, and `.join` on either throws MID-RENDER. There
 * is no ErrorBoundary in this app, so that blanks the whole SPA — off a
 * tooltip, on the screen whose empty table is a claim. And this tooltip now
 * renders in the unreadable case too, which is precisely the payload most
 * likely to carry a malformed neighbour.
 */
function unresolvedGroupList(
  snapshot: OpenerDescalceSnapshot | undefined
): string {
  const groupIds = snapshot?.unresolvedAccounts?.groupIds;
  if (!Array.isArray(groupIds)) return '';
  const named = groupIds.filter((id): id is string => typeof id === 'string');
  return named.length > 0 ? ` — group(s): ${named.join(', ')}` : '';
}

/**
 * Header strip: visible-row count, total descalce (contracts, NON-ambiguous
 * rows only — labelled as such), ambiguous/unread counts, what `groups ON` is
 * suppressing, what the sweeper excluded as resolved, the rules it could not
 * resolve an account pair for, and one freshness/source chip per
 * VENUE-ACCOUNT in the snapshot.
 *
 * 🚨 Every population this screen does NOT render has a counter here. An empty
 * table is a claim ("no naked legs"), and a row that disappears without a
 * number next to it makes that claim on the operator's behalf.
 */
export const DescalceHeaderStrip = ({
  totals,
  snapshot,
  resolvedExcluded,
  excludedUnreadable,
  groupsOnHidden,
  unresolvedRuleCount,
  unresolvedUnreadable,
  triage
}: {
  totals: DescalceTotals;
  snapshot: OpenerDescalceSnapshot | undefined;
  /** Rules the sweeper left out because a leg already resolved (plus any such
   *  row a pre-change backend still sent, which the page drops itself). */
  resolvedExcluded: number;
  /** The snapshot CARRIED a count and it could not be read (not an integer,
   *  negative, not a number). Absent is a different fact — an older backend,
   *  whose rows still arrive — and is NOT this. */
  excludedUnreadable: boolean;
  /** Live rows `groups ON` ALONE is suppressing — it is engaged by default,
   *  and it takes stopped, panicked and UNGROUPED rows off the table the
   *  operator lands on. Deliberately NOT the delta of every filter: that is
   *  dominated by the numeric floor and would bury this. */
  groupsOnHidden: number;
  /**
   * Rules the sweeper refused for an unresolvable venue-account pair, and
   * whether that count itself was unreadable.
   *
   * 🚨 Derived ONCE, by the page, and handed down — the empty-table message
   * says "see the chip above", so the two have to be the same number. Two
   * derivations of the same fact are two chances to disagree.
   */
  unresolvedRuleCount: number;
  unresolvedUnreadable: boolean;
  /** The triage index, straight from `useTriageIndex`. */
  triage: UseTriageIndexResult;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    flexWrap="wrap"
    sx={{ mb: 1 }}
  >
    <Tooltip title="Row count and total are scoped to the currently visible (filtered) rows — clear the filters to see every row the snapshot still carries. Rows excluded as resolved do not come back with any filter; they are counted in their own chip.">
      <Typography sx={{ fontSize: '0.75rem', color: 'grey.400' }}>
        {totals.rowCount} row{totals.rowCount === 1 ? '' : 's'} shown
      </Typography>
    </Tooltip>
    <Tooltip title="Sum of venue.descalce over non-ambiguous, fully-read, currently-visible (filtered) rows only — an ambiguous claimant carries the full market size, so including it would double-count, and a row with an unread leg has no number to add.">
      <Typography
        data-total-descalce={totals.totalDescalce}
        sx={{ fontSize: '0.75rem', color: 'grey.400' }}
      >
        Total descalce (shown, excl. ambiguous &amp; unread):{' '}
        <Box component="span" sx={{ fontWeight: 700, color: 'common.white' }}>
          {Number.isInteger(totals.totalDescalce)
            ? totals.totalDescalce
            : totals.totalDescalce.toFixed(2)}
        </Box>
      </Typography>
    </Tooltip>
    {totals.triageCount > 0 && (
      <Tooltip title="Visible rows carrying an unread triage card — a stuck counter on the rule, or an orphaned automation fill on the pair. Not gated on the row being descalzada now: a card still waiting for a human is open whatever the current NET reads.">
        <Chip
          size="small"
          label={`${totals.triageCount} with open counter/orphan`}
          color="warning"
          variant="outlined"
          data-triage-rows={totals.triageCount}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    <TriageIndexNotice
      index={triage.data}
      isStale={triage.isStale}
      lastSuccessAt={triage.lastSuccessAt}
    />
    {totals.ambiguousCount > 0 && (
      <Chip
        size="small"
        label={`${totals.ambiguousCount} ambiguous`}
        variant="outlined"
        sx={{ fontSize: '0.65rem', height: 20 }}
      />
    )}
    {totals.unreadCount > 0 && (
      <Tooltip title="Rows whose position could not be read on one of their venue-accounts. They are LEFT OUT of the total above — an unknown is not a zero — and every number on them renders as “—”.">
        <Chip
          size="small"
          label={`${totals.unreadCount} unread`}
          variant="outlined"
          color="warning"
          data-unread-rows={totals.unreadCount}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    {groupsOnHidden > 0 && (
      <Tooltip title="Rows that clear every other filter and are hidden ONLY by “groups ON”, which the screen opens with engaged: their group is stopped or PANICKED, or they have no group at all (the core engine's). A panicked group is exactly where a naked leg outlives the engine that was going to close it, so the count is here rather than left to be noticed. Release the toggle to see them.">
        <Chip
          size="small"
          label={`${groupsOnHidden} hidden by groups ON`}
          variant="outlined"
          color="warning"
          data-groups-on-hidden={groupsOnHidden}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    {/* 🚨 ONE chip, never both. An unreadable count next to a readable-looking
        total is two numbers about the same fact, and the smaller one would be
        read as the total when it is only a floor. */}
    {excludedUnreadable ? (
      <Tooltip title="The snapshot carried the sweeper's count of the rules it excluded for having a resolved leg, and it could not be read (not a number, not a whole number, or negative). Those rows are not here to recount, so the total is UNKNOWN — reporting it as zero would be this screen claiming nothing was excluded. Any number shown is only what THIS PAGE dropped itself: a floor, not the total.">
        <Chip
          size="small"
          label={
            resolvedExcluded > 0
              ? `≥${resolvedExcluded} resolved excluded (total unreadable)`
              : 'resolved excluded: unknown'
          }
          variant="outlined"
          color="warning"
          data-resolved-excluded-unknown="true"
          data-resolved-excluded-floor={resolvedExcluded}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    ) : (
      resolvedExcluded > 0 && (
        <Tooltip title="Rules whose leg already resolved at its venue — Kalshi settled it, or Polymarket flagged it redeemable. They are left out of this screen because a resolved leg cannot be hedged against any more. Counted over the WHOLE snapshot and before any filter, so it does not narrow with the account or group scoping above.">
          <Chip
            size="small"
            label={`${resolvedExcluded} resolved excluded`}
            variant="outlined"
            data-resolved-excluded={resolvedExcluded}
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Tooltip>
      )
    )}
    {(unresolvedRuleCount > 0 || unresolvedUnreadable) && (
      <Tooltip
        title={`Rules the sweeper REFUSED to render because their venue-account pair could not be resolved${unresolvedGroupList(
          snapshot,
        )}. They hold positions nobody is watching here; fix the group's accounts to bring them back.`}
      >
        <Chip
          size="small"
          label={
            unresolvedUnreadable
              ? 'rules with unresolved accounts: unknown'
              : `${unresolvedRuleCount} rule(s) with unresolved accounts`
          }
          variant="outlined"
          color="error"
          data-unresolved-rules={unresolvedUnreadable ? undefined : unresolvedRuleCount}
          data-unresolved-rules-unknown={unresolvedUnreadable ? 'true' : undefined}
          sx={{ fontSize: '0.65rem', height: 20 }}
        />
      </Tooltip>
    )}
    <Box sx={{ flex: 1 }} />
    {snapshotAccounts(snapshot).map(account => (
      <AccountChip
        key={venueAccountKey(account.platform, account.accountId)}
        account={account}
      />
    ))}
  </Stack>
);
